import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { clearMaterialRegistryReport, generateMaterialRegistryReport } from '../../../../../redux/Project/materialsData/action';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';

interface GenerateProductionAssemblyReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerated?: () => void;
  projectId: number | string;
  projectName: string;
  jobNumber: string;
}

export const GenerateProductionAssemblyReportModal: React.FC<GenerateProductionAssemblyReportModalProps> = ({
  isOpen,
  onClose,
  onGenerated,
  projectId,
  projectName,
  jobNumber,
}) => {
  const dispatch = useDispatch();
  const {
    materialRegistryReportLoading,
    materialRegistryReportSuccess,
    materialRegistryReportData,
    materialRegistryReportError,
  } = useSelector((state: any) => state.materialsData || {});

  const numericProjectId = useMemo(() => {
    const value = typeof projectId === 'string' ? parseInt(projectId, 10) : Number(projectId);
    return Number.isFinite(value) ? value : 0;
  }, [projectId]);

  const [location, setLocation] = useState('');
  const [reportNo, setReportNo] = useState('');
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);
  const handledSuccessRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      dispatch(clearMaterialRegistryReport());
      setLocation('');
      setReportNo('');
      setViewerFile(null);
      handledSuccessRef.current = false;
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen || !materialRegistryReportSuccess || !materialRegistryReportData || handledSuccessRef.current) return;
    handledSuccessRef.current = true;

    const fileUrl =
      materialRegistryReportData?.downloadUrl ||
      materialRegistryReportData?.data?.downloadUrl ||
      materialRegistryReportData?.fileUrl ||
      materialRegistryReportData?.data?.fileUrl ||
      materialRegistryReportData?.Location ||
      materialRegistryReportData?.data?.Location;
    const fileName =
      materialRegistryReportData?.fileName ||
      materialRegistryReportData?.data?.fileName ||
      'Production Assembly Report';

    onGenerated?.();
    if (fileUrl) {
      setViewerFile({ fileUrl, fileName });
    }
  }, [isOpen, materialRegistryReportSuccess, materialRegistryReportData, onGenerated]);

  const handleClose = () => {
    dispatch(clearMaterialRegistryReport());
    setViewerFile(null);
    onClose();
  };

  const handleGenerate = () => {
    if (!numericProjectId) {
      customNotification.error('Validation Error', 'Project ID is required.');
      return;
    }
    if (!reportNo.trim()) {
      customNotification.error('Validation Error', 'Report number is required.');
      return;
    }
    if (!location.trim()) {
      customNotification.error('Validation Error', 'Location is required.');
      return;
    }

    dispatch(generateMaterialRegistryReport({
      reportType: REPORT_TYPES.PRODUCTION_ASSEMBLY_REPORT,
      projectId: numericProjectId,
      reportNo: reportNo.trim(),
      location: location.trim(),
    }));
  };

  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={() => setViewerFile(null)}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[560px] w-[95vw] p-0 gap-0 overflow-hidden bg-page-bg">
        <DialogHeader className="flex-shrink-0 pl-4 sm:pl-6 pr-14 py-4 border-b border-[#E2E5EA] bg-white flex flex-row items-center gap-4">
          <Button
            onClick={handleClose}
            variant="outline"
            size="sm"
            className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
          >
            <ArrowLeft className="h-4 w-4 text-text-grey" />
          </Button>
          <div className="min-w-0">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Generate Production Assembly Report
            </DialogTitle>
            <p className="text-[12px] text-heritage-grey mt-1 truncate">
              Project: {projectName}{jobNumber ? <span className="ml-2">- Job: {jobNumber}</span> : null}
            </p>
          </div>
        </DialogHeader>

        <div className="p-4 sm:p-6 bg-white space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assembly-report-no" className="text-[12px] font-medium text-text-grey">
              Report Number *
            </Label>
            <Input
              id="assembly-report-no"
              value={reportNo}
              onChange={(event) => setReportNo(event.target.value)}
              placeholder="Enter report number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="assembly-location" className="text-[12px] font-medium text-text-grey">
              Location *
            </Label>
            <Input
              id="assembly-location"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              placeholder="Enter location"
            />
          </div>

          {materialRegistryReportError ? (
            <div className="text-[12px] text-hero-red bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg p-3">
              {materialRegistryReportError}
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="border-[#E2E5EA] text-text-grey hover:bg-page-bg"
              onClick={handleClose}
              disabled={materialRegistryReportLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleGenerate}
              className="bg-hero-red hover:bg-hero-red-60 text-white"
              disabled={materialRegistryReportLoading}
            >
              {materialRegistryReportLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
